import React, { useState, useEffect } from "react";
import { useResource, useFetcher } from "rest-hooks";
import { makeStyles } from "@material-ui/styles";
import { Grid, Typography, Button, TextField, Theme } from "@material-ui/core";
import FaceIcon from "@material-ui/icons/Face";
import CloseIcon from "@material-ui/icons/Close";
import { IconButton } from "@material-ui/core";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { navigate } from "@reach/router";
import { FlyOutMenu, FlyOutMenuProps } from "./FlyOutMenuRight";
import { FormattedMessage } from "react-intl";
import { UserResource } from "../../../../resources";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import * as Yup from "yup";
import { useFormik } from "formik";
import { MyPageButton, SnackbarContentWrapper } from "../../../common";
import { useCommonWidths } from "../../../../hooks";
import { useSnackbar } from "notistack";
import {
	regExCobraMobile,
	regExCobraEmail,
	headerHeightMobile,
} from "../../../../constants";

const itemPadding = 24;
const useStyles = makeStyles((theme: Theme) => ({
	logoutButton: {
		width: "100%",
		padding: itemPadding,
		[theme.breakpoints.down("sm")]: {
			padding: 10,
		},
	},
	standardPadding: {
		padding: itemPadding,
		[theme.breakpoints.down("sm")]: {
			padding: 13,
		},
	},
	grayBackground: { backgroundColor: "#F2F5F8" },
	logoutText: { alignSelf: "center", textAlign: "end" },
	logoutIcon: {
		paddingLeft: theme.spacing(1),
		alignSelf: "center",
		fontSize: 45,
	},
	userNameContainer: {
		paddingLeft: theme.spacing(1),
		alignSelf: "center",
	},
	headerContainer: {
		paddingLeft: theme.spacing(2),
		paddingRight: 0,
		backgroundColor: "#F2F5F8",
		display: "flex",
	},
	headerIconContainer: { textAlign: "center", alignSelf: "center" },
	profileIconSize: { fontSize: 38 },
	closeIconContainer: {
		textAlign: "center",
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		[theme.breakpoints.down("sm")]: {
			height: "100%",
			borderTop: "none",
		},
	},
	editButtonStyle: {
		textDecoration: "underline",
		color: "darkblue",
		[theme.breakpoints.down("sm")]: {
			fontSize: "smaller",
		},
	},
	consentsButtonStyle: {
		[theme.breakpoints.down("sm")]: {
			marginTop: 10,
		},
	},
	logoutButtonMarginBottom: {
		margin: `${theme.spacing(2)}px 0 `,
		[theme.breakpoints.down("sm")]: {
			marginBottom: 70,
		},
	},
	saveButton: { marginTop: theme.spacing(2) },
	myProfileContainer: {
		height: "100%",
		[theme.breakpoints.down("sm")]: {
			height: window.innerHeight - headerHeightMobile,
		},
	},
	myProfileInnerContainer: {
		minHeight: "calc(100% - 82px)",
	},
	section: {
		marginTop: 20,
		"&:first-child": {
			marginTop: 0
		}
	},
	sectionTitle: {
		fontWeight: "bold"
	}
}));

interface MyProfileProps extends FlyOutMenuProps {
	toggleMenu: () => void;
	open: boolean;
}
export const MyProfile = (props: MyProfileProps) => {
	const classes = useStyles();

	const [editMode, setEditMode] = useState(false);
	const { fullName, ...user } = useResource(UserResource.detailShape(), {});

	const [mobilePhone, setMobilePhone] = useState(user.mobilePhone);
	const [email, setEmail] = useState(user.email);
	const [streetAddress, setStreetAddress] = useState(user.streetAddress || "");
	const [postalCode, setPostalCode] = useState(user.postalCode || "");
	const [city, setCity] = useState(user.city || "");

	const onSave = () => {
		setStreetAddress(values.streetAddress);
		setPostalCode(values.postalCode);
		setCity(values.city);
		setEmail(values.email);
		setMobilePhone(values.mobilePhone);
	};

	const toggleEdit = () => {
		setEditMode(!editMode);
	};
	const isMobile = useCommonWidths().isSmDown;

	const validationSchema = Yup.object({
		streetAddress: Yup.string().nullable().required("Vennlist fyll ut"),
		postalCode: Yup.string()
			.length(4, "Postnummer må være 4 siffer")
			.nullable()
			.required("Vennlist fyll ut"),
		city: Yup.string().nullable().required("Vennlist fyll ut"),
		email: Yup.string()
			.nullable()
			.matches(
				new RegExp(regExCobraEmail),
				"Vennligst skriv inn en gyldig epostadresse"
			)
			.required("Vennligst fyll ut"),
		mobilePhone: Yup.string()
			.nullable()
			.matches(
				new RegExp(regExCobraMobile),
				"Mobilnummeret kan ikke inneholde spesialtegn, bokstaver, eller starte med 0"
			)
			.min(8, "Mobilnummer må minst inneholde 8 tegn")
			.required("Vennligst fyll ut"),
	});
	const updateUserInfo = useFetcher(UserResource.partialUpdateShape());

	const { enqueueSnackbar } = useSnackbar();

	const { handleSubmit, handleChange, errors, values, setValues } = useFormik({
		initialValues: {
			streetAddress: user.streetAddress,
			postalCode: user.postalCode,
			city: user.city,
			email: user.email,
			mobilePhone: user.mobilePhone,
		},
		validationSchema,
		onSubmit(values: any) {
			updateUserInfo({}, values)
				.then(() => {
					setEditMode(false);
					onSave();
				})
				.catch((err) => {
					if (err.toString().includes("Unauthorized")) {
						enqueueSnackbar("", {
							key: "unauthorizedError",
							content: (key: any) => (
								<SnackbarContentWrapper
									variant="warning"
									message={
										<FormattedMessage
											id="minside.invoicestabpage.unauth_message"
											defaultMessage="Sesjonen har utløpt. Vennligst logg inn på nytt"
											description="Session expired"
										/>
									}
									id={key}
								/>
							),
						});
						navigate("/logout", { state: { errorCode: 401 } });
					} else {
						enqueueSnackbar("", {
							key: "updateError",
							autoHideDuration: 1500,
							content: (key) => (
								<SnackbarContentWrapper
									variant="error"
									message={
										<FormattedMessage
											id="minside.myprofile.save_data_error"
											defaultMessage="Feil ved lagring av data"
											description="Unsuccessfull data update"
										/>
									}
									id={key}
								/>
							),
						});
					}
				});
		},
	});

	useEffect(() => {
		setValues({
			mobilePhone: mobilePhone?.split(" ")?.join(""),
			streetAddress: streetAddress,
			postalCode: postalCode,
			city: city,
			email: email,
		});
	}, [
		mobilePhone,
		editMode,
		streetAddress,
		postalCode,
		city,
		email,
		setValues,
	]);

	const renderEditButton = () => {
		if (isMobile){
			return editMode
				? <CloseIcon />
				: <EditIcon />
		}
		return editMode
			? (
				<FormattedMessage
					id="minside.myprofile.close_edit"
					defaultMessage="Lukk"
					description="Close profile information"
				/>
			) : (
				<FormattedMessage
					id="minside.myprofile.edit"
					defaultMessage="Rediger"
					description="Edit profile information"
				/>

			)
	}

	return (
		<FlyOutMenu {...props}>
			<Grid className={classes.myProfileContainer}>
				<GridHeader navn={fullName} classes={classes} {...props} />
				<Grid
					item
					container
					direction="column"
					justify="space-between"
					className={classes.myProfileInnerContainer}
				>
					<Grid item>
						<Grid item className={classes.standardPadding}>
							<MyPageButton
								onClick={() => {
									navigate("/consents");
									props.toggleMenu();
								}}
								className={classes.consentsButtonStyle}
								nextIcon
							>
								<FormattedMessage
									id="minside.faq.my_consents"
									defaultMessage="Mine samtykker"
									description="My consents link text"
								/>
							</MyPageButton>
						</Grid>
						{user.existsInBackoffice && (
							<>
								<hr />
								<form onSubmit={handleSubmit}>
									<Grid container direction="row" className={classes.standardPadding} justify="space-between">
										<Grid item>
											<Typography variant="h6">
												<FormattedMessage
													id="minside.myprofile.myInformation"
													defaultMessage="Min informasjon"
													description="My information header"
												/>
											</Typography>
										</Grid>
										<Grid item>
											<MyPageButton
												className={classes.editButtonStyle}
												onClick={() => toggleEdit()}
											>
												{renderEditButton()}
											</MyPageButton>
										</Grid>
									</Grid>
									<Grid container direction="column" item spacing={1} className={classes.standardPadding}>
										<Grid container item className={classes.section} spacing={1}>
											<Grid item>
												<Typography variant="h6" className={classes.sectionTitle}>
													<FormattedMessage
														id="minside.myprofile.address"
														defaultMessage="Adresse"
														description="Address header my profile"
													/>
												</Typography>
											</Grid>
											<Grid item xs={12}>
												{editMode ? (
													<TextField
														name="streetAddress"
														error={Boolean(errors.streetAddress)}
														helperText={errors.streetAddress ?? ""}
														fullWidth
														variant="outlined"
														value={values.streetAddress ?? ""}
														label="Gateadresse"
														onChange={(e) => {handleChange(e)}}
													/>
												) : (
													<>
														<Typography>{user.streetAddress}</Typography>
														<Typography>{user.streetAddress2}</Typography>
													</>
												)}
											</Grid>
											<Grid container item direction="row" spacing={1}>
												<Grid item xs={editMode ? 6 : undefined}>
													{editMode ? (
														<TextField
															name="postalCode"
															error={Boolean(errors.postalCode)}
															helperText={errors.postalCode ?? ""}
															fullWidth
															variant="outlined"
															value={values.postalCode ?? ""}
															label={isMobile ? "Postnr." : "Postnummer"}
															onChange={(e) => {handleChange(e)}}
														/>
													) : (
														<Typography>{user.postalCode}</Typography>
													)}
												</Grid>
												<Grid item xs={editMode ? 6 : undefined}>
													{editMode ? (
														<TextField
															name="city"
															error={Boolean(errors.city)}
															helperText={errors.city ?? ""}
															fullWidth
															variant="outlined"
															value={values.city ?? ""}
															onChange={(e) => {handleChange(e)}}
															label="Poststed"
														/>
													) : (
														<Typography>{user.city}</Typography>
													)}
												</Grid>
											</Grid>
										</Grid>
										<Grid container item className={classes.section} spacing={1}>
											<Grid item>
												<Typography variant="h6" className={classes.sectionTitle}>
													<FormattedMessage
														id="minside.myprofile.email"
														defaultMessage="E-post"
														description="Email header my profile"
													/>
												</Typography>
											</Grid>
											<Grid item xs={12}>
												{editMode ? (
													<TextField
														name="email"
														error={Boolean(errors.email)}
														helperText={errors.email ?? ""}
														fullWidth
														variant="outlined"
														value={values.email ?? ""}
														label="E-post"
														onChange={e => {handleChange(e)}}
													/>
												) : (
													<Typography>{user.email}</Typography>
												)}
											</Grid>
										</Grid>
										<Grid container item className={classes.section} spacing={1}>
											<Grid item>
												<Typography variant="h6" className={classes.sectionTitle}>
													<FormattedMessage
														id="minside.myprofile.mobilePhone"
														defaultMessage="Telefon"
														description="Mobilephone header my profile"
													/>
												</Typography>
											</Grid>
											<Grid item xs={12}>
												{editMode ? (
													<TextField
														name="mobilePhone"
														error={Boolean(errors.mobilePhone)}
														helperText={errors.mobilePhone ?? ""}
														fullWidth
														variant="outlined"
														value={values.mobilePhone ?? ""}
														label="Telefonnummer"
														onChange={(e) => handleChange(e)}
													/>
												) : (
													<Typography>{user.mobilePhone}</Typography>
												)}
											</Grid>
										</Grid>
										{editMode && (
											<Grid item>
												<SaveButton
													disabled={Boolean(
														errors.streetAddress ||
														errors.city ||
														errors.postalCode ||
														errors.email ||
														errors.mobilePhone
													)}
													classes={classes}
												/>
											</Grid>
										)}
									</Grid>
								</form>
							</>
						)}
					</Grid>
					<Grid item>
						<GridLogoutButton classes={classes} />
					</Grid>
				</Grid>
			</Grid>
		</FlyOutMenu>
	);
};

const GridHeader = (props: any) => {
	const { toggleMenu, classes, navn } = props;
	const isMobile = useCommonWidths().isSmDown;

	return (
		<Grid className={classes.headerContainer} item>
			<Grid className={classes.headerIconContainer} item xs={2}>
				<FaceIcon className={classes.profileIconSize} />
			</Grid>
			<Grid item xs={7} className={classes.userNameContainer}>
				<Grid item>
					<Typography
						variant={isMobile ? "h6" : "h5"}
						noWrap
						style={{
							width: "max-content",
							maxWidth: "100%",
						}}
					>
						{navn}
					</Typography>
				</Grid>
				<Grid item>
					<Typography
						variant="caption"
						style={isMobile ? { fontSize: "0.7rem" } : {}}
					>
						<FormattedMessage
							id="minside.myprofile.my_profile"
							defaultMessage="Min Profil"
							description="My profile text"
						/>
					</Typography>
				</Grid>
			</Grid>
			<Grid className={classes.closeIconContainer} item xs={3}>
				<IconButton onClick={() => toggleMenu()}>
					<CloseIcon />
				</IconButton>
			</Grid>
		</Grid>
	);
};
const GridLogoutButton = (props: any) => {
	const { classes } = props;

	return (
		<Grid
			container
			justify="center"
			className={clsx(classes.grayBackground, classes.logoutButtonMarginBottom)}
		>
			<Button
				className={classes.logoutButton}
				onClick={() => navigate("/logout")}
			>
				<Typography variant="h4" className={classes.logutText}>
					<FormattedMessage
						id="minside.myprofile.log_out"
						defaultMessage="Logg ut"
						description="Log out button text"
					/>
				</Typography>
				<ExitToAppIcon className={classes.logoutIcon} />
			</Button>
		</Grid>
	);
};

interface SaveButtonProps {
	classes: any;
	disabled: boolean;
	onClick?: () => void;
}
const SaveButton = (props: SaveButtonProps) => {
	const { classes, onClick, disabled } = props;
	return (
		<Button
			fullWidth
			size="large"
			type="submit"
			color="primary"
			variant="contained"
			className={classes.saveButton}
			disabled={disabled}
			onClick={onClick}
		>
			<FormattedMessage
				id="minside.myprofile.save"
				defaultMessage="Lagre endringer"
				description="Save changes button my profile"
			/>
		</Button>
	);
};
